import { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers';
import cn from 'classnames';
// Hooks
import { useCountdown } from 'hooks';
// Ui3
import Text from 'ui3/Text/Text';
import OTPInput from 'ui3/OTPInput/OTPInput';
import Button from 'ui3/Button/Button';
// Styles
import styles from './OTPForm.module.scss';

export const COUNTDOWN_TIME = 60;

export type OTPFormData = {
  otpCode: string;
};

export type OTPFormProps = {
  description: string;
  onSubmit: (values: OTPFormData) => void;
  onVerifyAnotherWay?: () => void;
  onResendCode?: () => void;
  contactType?: 'email' | 'phone';
  contactValue?: string;
  manuallySendCode?: boolean;
  initialTimeLeft?: number;
};

const getValidationSchema = () => {
  return yup.object().shape({
    otpCode: yup
      .string()
      .required('OTP is required')
      .length(5, 'Please enter all 5 digits'),
  });
};

const OTPForm = ({
  description,
  onSubmit,
  onVerifyAnotherWay,
  onResendCode,
  contactValue = '',
  initialTimeLeft = 0,
}: OTPFormProps) => {
  const methods = useForm<OTPFormData>({
    resolver: yupResolver(getValidationSchema()),
    defaultValues: {
      otpCode: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const [codeAlreadySent, setCodeAlreadySent] = useState<boolean>(false);
  const { isActive: isCountdownActive, formatTime, start } = useCountdown();
  const otpCode = methods.watch('otpCode');

  // Start countdown when code is sent
  useEffect(() => {
    if (!codeAlreadySent) {
      start(initialTimeLeft || COUNTDOWN_TIME);
      setCodeAlreadySent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeAlreadySent, setCodeAlreadySent, initialTimeLeft]);

  const handleResendCode = () => {
    onResendCode?.();
    methods.reset({
      otpCode: '',
    });
    setCodeAlreadySent(true);
    start(COUNTDOWN_TIME);
  };

  const handleFormSubmit = methods.handleSubmit(async (values: OTPFormData) => {
    try {
      onSubmit(values);
    } catch (error) {
      console.log(error);
    }
  });

  const renderResendCode = () => {
    if (isCountdownActive) {
      return (
        <>
          <Text className={styles.countdownTimer} color="lights-low">
            {formatTime()}
          </Text>
          <Text
            className={cn(styles.resendCodeText, {
              [styles.disabled]: isCountdownActive,
            })}
            color="accent-dim"
          >
            Send Code
          </Text>
        </>
      );
    }

    return (
      <>
        <Text>Didn&apos;t get the code?</Text>
        <Text
          color="accent-default"
          className={styles.resendCodeText}
          onClick={handleResendCode}
        >
          Resend Code
        </Text>
      </>
    );
  };

  return (
    <div className={styles.root}>
      <Text variant="body1Regular16" className={styles.description}>
        {description} {contactValue && <strong>{contactValue}</strong>}
      </Text>
      <Controller
        control={methods.control}
        name="otpCode"
        render={({ onChange, value, name, ref }) => (
          <OTPInput
            value={value}
            onChange={onChange}
            name={name}
            ref={ref}
            numInputs={5}
            error={!!methods.errors.otpCode}
            errorMessage={methods.errors.otpCode?.message}
            shouldAutoFocus
            inputType="number"
          />
        )}
      />
      <div className={styles.resendCodeContainer}>{renderResendCode()}</div>
      <div className={styles.footer}>
        {onVerifyAnotherWay && (
          <Button
            variant="primary"
            subVariant="text"
            size="medium"
            onClick={onVerifyAnotherWay}
          >
            Verify Another Way
          </Button>
        )}
        <Button
          variant="primary"
          size="medium"
          disabled={!!methods.errors.otpCode || otpCode.length < 5}
          onClick={handleFormSubmit}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default OTPForm;
